/**
 * FloatingToolbox Component
 * A draggable, resizable container for the markup editor toolbar.
 * - Drag by header only
 * - Resize via bottom-right handle
 * - Clamped to viewport bounds
 * - Persists position/size in localStorage
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GripHorizontal } from 'lucide-react';

const STORAGE_KEY = 'collection-markup-toolbox-state';

// Default dimensions and position
const DEFAULT_STATE = {
  x: 16,
  y: null, // null means "center vertically"
  width: 180,
  height: 320
};

// Minimum dimensions
const MIN_WIDTH = 150;
const MIN_HEIGHT = 200;

// Header height for drag area
const HEADER_HEIGHT = 28;

const FloatingToolbox = ({ children, isVisible }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState({ x: DEFAULT_STATE.x, y: DEFAULT_STATE.y });
  const [size, setSize] = useState({ width: DEFAULT_STATE.width, height: DEFAULT_STATE.height });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [initialResizeState, setInitialResizeState] = useState(null);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Validate and apply saved state
        if (parsed.x !== undefined && parsed.y !== undefined) {
          setPosition({ x: parsed.x, y: parsed.y });
        }
        if (parsed.width !== undefined && parsed.height !== undefined) {
          setSize({ 
            width: Math.max(MIN_WIDTH, parsed.width), 
            height: Math.max(MIN_HEIGHT, parsed.height) 
          });
        }
      }
    } catch (e) {
      console.warn('Failed to load toolbox state from localStorage:', e);
    }
  }, []);

  // Save state to localStorage whenever position/size changes
  useEffect(() => {
    if (position.y !== null) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          x: position.x,
          y: position.y,
          width: size.width,
          height: size.height
        }));
      } catch (e) {
        console.warn('Failed to save toolbox state to localStorage:', e);
      }
    }
  }, [position, size]);

  // Clamp position to viewport bounds
  const clampPosition = useCallback((x, y, w, h) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Ensure at least 50px is visible
    const minVisible = 50;
    
    const clampedX = Math.max(0, Math.min(x, viewportWidth - minVisible));
    const clampedY = Math.max(0, Math.min(y, viewportHeight - HEADER_HEIGHT));
    
    return { x: clampedX, y: clampedY };
  }, []);

  // Clamp size to viewport bounds
  const clampSize = useCallback((w, h, x, y) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Don't let width extend beyond viewport
    const maxWidth = viewportWidth - x;
    const maxHeight = viewportHeight - y;
    
    const clampedWidth = Math.max(MIN_WIDTH, Math.min(w, maxWidth));
    const clampedHeight = Math.max(MIN_HEIGHT, Math.min(h, maxHeight));
    
    return { width: clampedWidth, height: clampedHeight };
  }, []);

  // Calculate initial centered Y position if not set
  useEffect(() => {
    if (position.y === null && isVisible) {
      const viewportHeight = window.innerHeight;
      const centeredY = Math.max(0, (viewportHeight - size.height) / 2);
      setPosition(prev => ({ ...prev, y: centeredY }));
    }
  }, [isVisible, position.y, size.height]);

  // Handle window resize - re-clamp position
  useEffect(() => {
    const handleResize = () => {
      if (position.y !== null) {
        const clamped = clampPosition(position.x, position.y, size.width, size.height);
        if (clamped.x !== position.x || clamped.y !== position.y) {
          setPosition(clamped);
        }
        const clampedSize = clampSize(size.width, size.height, clamped.x, clamped.y);
        if (clampedSize.width !== size.width || clampedSize.height !== size.height) {
          setSize(clampedSize);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, size, clampPosition, clampSize]);

  // Drag handlers
  const handleDragStart = useCallback((e) => {
    // Only drag from header area
    if (e.target.closest('[data-resize-handle]')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    setIsDragging(true);
    setDragOffset({
      x: clientX - position.x,
      y: clientY - (position.y ?? 0)
    });
  }, [position]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;
    
    const clamped = clampPosition(newX, newY, size.width, size.height);
    setPosition(clamped);
  }, [isDragging, dragOffset, size, clampPosition]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    setIsResizing(true);
    setInitialResizeState({
      mouseX: clientX,
      mouseY: clientY,
      width: size.width,
      height: size.height
    });
  }, [size]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !initialResizeState) return;
    
    e.preventDefault();
    
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    const deltaX = clientX - initialResizeState.mouseX;
    const deltaY = clientY - initialResizeState.mouseY;
    
    const newWidth = initialResizeState.width + deltaX;
    const newHeight = initialResizeState.height + deltaY;
    
    const clamped = clampSize(newWidth, newHeight, position.x, position.y ?? 0);
    setSize(clamped);
  }, [isResizing, initialResizeState, position, clampSize]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setInitialResizeState(null);
  }, []);

  // Global mouse/touch event listeners for drag and resize
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleUp = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleUp);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      const handleMove = (e) => handleResizeMove(e);
      const handleUp = () => handleResizeEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleUp);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  if (!isVisible) return null;
  if (position.y === null) return null; // Wait for initial position calculation

  return (
    <div
      ref={containerRef}
      className="fixed z-50 flex flex-col bg-stone-800 rounded-lg shadow-xl border border-slate-700"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: isDragging || isResizing ? 'none' : 'auto'
      }}
      data-testid="floating-toolbox"
    >
      {/* Draggable Header */}
      <div
        className="flex items-center justify-center h-7 bg-slate-700 rounded-t-lg cursor-grab active:cursor-grabbing border-b border-slate-600 shrink-0"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        data-testid="toolbox-drag-header"
      >
        <GripHorizontal className="w-4 h-4 text-slate-400" />
        <span className="ml-1 text-xs text-slate-400 select-none">Drag to move</span>
      </div>

      {/* Scrollable Content Area */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ minHeight: 0 }}
        data-testid="toolbox-content"
      >
        {children}
      </div>

      {/* Resize Handle (bottom-right corner) */}
      <div
        data-resize-handle="true"
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        data-testid="toolbox-resize-handle"
      >
        <svg
          className="w-4 h-4 text-slate-500 hover:text-slate-300 transition-colors"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M14 14H10V12H12V10H14V14Z" />
          <path d="M14 8H12V6H14V8Z" />
          <path d="M8 14V12H6V14H8Z" />
        </svg>
      </div>
    </div>
  );
};

export default FloatingToolbox;
